import EntityExampleWrapper from 'client/components/core/EntityExampleWrapper'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import SelectNewUi from 'common/components/core/Select/ui/select-new'
import CommonField from 'common/components/entities/Field/FieldUi'
import FieldWrapperUi from 'common/components/entities/Field/FieldWrapperUi'
import FieldIconUiNew from 'common/components/entities/Field/field-icon-ui'
import PhoneNumberFieldUiNew from 'common/components/entities/Field/phone-number-field-ui'
import { slugs } from 'common/constants/inputTypes'
import { BaseFieldInterface } from 'common/types/entities/field-interface'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useContactFields } from 'src/editor/shared/hooks/use-contact-fields'
import { useFormField } from 'src/editor/shared/hooks/use-form-fields'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { getContactFieldTitle } from 'src/editor/shared/utils/get-contact-field-title'

interface BaseEditorFieldProps {
  entity: BaseFieldInterface
}

export const BaseEditorField = ({ entity }: BaseEditorFieldProps) => {
  const { t } = useTranslation()
  const { data: contactFields } = useContactFields()
  const globalFontFamily = usePage(pageSelectors.getGlobalFontFamily)
  const [inputTypeName, setInputTypeName] = React.useState(
    t('entities.field.warnings.no_input_name'),
  )

  useEffect(() => {
    if (
      entity.slug === slugs.PASSWORD ||
      entity.slug === slugs.CONFIRM_PASSWORD
    ) {
      setInputTypeName('PASSWORD')
      return
    }

    if (entity.slug && contactFields?.length) {
      const field = contactFields.find(({ slug }) => slug === entity.slug)

      if (field) {
        setInputTypeName(getContactFieldTitle(field, t))
      }
    } else {
      setInputTypeName(t('entities.field.warnings.no_input_name'))
    }
  }, [t, entity.slug, contactFields])

  const fontFamily =
    globalFontFamily === 'inherit' ? 'initial' : globalFontFamily // otherwise it inherits Editor's UI Averta

  const iconColor = entity.color || 'initial'
  const mobileIconColor = entity.mobileColor || iconColor

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
  } = useFormField({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: fontFamily,
    mobileFontFamily: fontFamily,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
  })

  const { formFields } = useThemeColor()

  const formFieldBackgroundColor = formFields?.formFieldBackgroundColor
  const formFieldTextColor = formFields?.formFieldTextColor
  const formFieldBorderColor = formFields?.formFieldBorderColor

  if (entity.slug === slugs.COUNTRY) {
    return (
      <SelectNewUi
        placeholderColor={entity.placeholderColor}
        mobilePlaceholderColor={entity.mobilePlaceholderColor}
        fontFamily={calculatedFontFamily.desktop}
        textAlign={calculatedTextAlign.desktop}
        mobileTextAlign={calculatedTextAlign.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        border={{
          ...calculatedBorder.desktop,
          color: calculatedBorder.desktop.color || formFieldBorderColor,
        }}
        mobileBorder={{
          ...calculatedBorder.mobile,
          color: calculatedBorder.mobile.color || formFieldBorderColor,
        }}
        backgroundColor={entity.backgroundColor || formFieldBackgroundColor}
        mobileBackgroundColor={
          entity.mobileBackgroundColor || formFieldBackgroundColor
        }
        color={entity.color || formFieldTextColor}
        mobileColor={entity.mobileColor || formFieldTextColor}
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        boxShadow={entity.boxShadow}
        mobileBoxShadow={entity.mobileBoxShadow}
      >
        <option>{t('entities.field.country.placeholder')}</option>
      </SelectNewUi>
    )
  }

  return (
    <EntityExampleWrapper
      label={inputTypeName}
      warning={Boolean(inputTypeName) === false}
    >
      <FieldWrapperUi>
        {entity.iconClassName && (
          <FieldIconUiNew
            color={iconColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            mobileColor={mobileIconColor}
            className={entity.iconClassName}
          />
        )}
        {entity.slug === slugs.PHONE_NUMBER ? (
          <PhoneNumberFieldUiNew
            fontFamily={calculatedFontFamily.desktop}
            hasIcon={!!entity.iconClassName}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            border={{
              ...calculatedBorder.desktop,
              color: calculatedBorder.desktop.color || formFieldBorderColor,
            }}
            mobileBorder={{
              ...calculatedBorder.mobile,
              color: calculatedBorder.mobile.color || formFieldBorderColor,
            }}
            backgroundColor={entity.backgroundColor || formFieldBackgroundColor}
            mobileBackgroundColor={
              entity.mobileBackgroundColor || formFieldBackgroundColor
            }
            color={entity.color || formFieldTextColor}
            mobileColor={entity.mobileColor || formFieldTextColor}
            placeholder={entity.placeholder}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
            inputProps={{ readOnly: true }}
            countrySelectorProps={{
              buttonProps: { onClick: undefined },
              buttonStyle: { cursor: 'default' },
            }}
          />
        ) : (
          <CommonField
            fontFamily={calculatedFontFamily.desktop}
            placeholder={entity.placeholder}
            padding={calculatedPadding.desktop}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            mobilePadding={calculatedPadding.mobile}
            id={entity.htmlAttrId}
            border={{
              ...calculatedBorder.desktop,
              color: calculatedBorder.desktop.color || formFieldBorderColor,
            }}
            mobileBorder={{
              ...calculatedBorder.mobile,
              color: calculatedBorder.mobile.color || formFieldBorderColor,
            }}
            backgroundColor={entity.backgroundColor || formFieldBackgroundColor}
            mobileBackgroundColor={
              entity.mobileBackgroundColor || formFieldBackgroundColor
            }
            color={entity.color || formFieldTextColor}
            mobileColor={entity.mobileColor || formFieldTextColor}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
          />
        )}
      </FieldWrapperUi>
    </EntityExampleWrapper>
  )
}
